import { useQuery } from '@tanstack/react-query'
import { can } from '@kronos/domain'
import type { Action } from '@kronos/domain'
import { repository } from '../../data'
import type { ItemUnit } from '../../data/inventory'

/**
 * The few things Inventory and Receive both need: who may do what, and how a
 * time and a place are said.
 */

/** `can()` for the person signed in. Hides controls; the server is the boundary. */
export function useCan(): (action: Action) => boolean {
  const me = useQuery({ queryKey: ['me'], queryFn: () => repository.me() })
  const roles = me.data?.roles ?? []
  return (action) => can(roles, action)
}

/** The server's sentence without the status the transport puts in front of it. */
export function errorText(e: unknown): string {
  return e instanceof Error ? e.message.replace(/^\d+:\s*/, '') : 'Something went wrong.'
}

/** "Today 14:32", "Yesterday 09:10", "3 Oct", "3 Oct 2025" — local wall clock. */
export function whenText(iso: string, now: Date = new Date()): string {
  const at = new Date(iso)
  const time = at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  const day = (d: Date): string => d.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (day(at) === day(now)) return `Today ${time}`
  if (day(at) === day(yesterday)) return `Yesterday ${time}`
  return at.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** Where a unit is, as somebody looking for it would say it. */
export function whereText(unit: ItemUnit): string {
  if (unit.fittedTo) {
    const into = unit.fittedTo.fleetNumber ?? `${unit.fittedTo.parentType} ${unit.fittedTo.parentSerial}`
    return `Fitted · ${into} · ${unit.fittedTo.position}`
  }
  switch (unit.location) {
    case 'spares':
      return 'On the shelf'
    case 'depot':
      return 'At the depot'
    case 'scrapped':
      return 'Scrapped'
    case 'installed':
      return 'Fitted'
    default:
      return 'Unknown'
  }
}

/** The tracking question, answered in the words the New item form asked it in. */
export function trackingText(tracking: 'serialized' | 'quantity', unit: string): string {
  if (tracking === 'serialized') return 'Each one has its own serial number'
  return unit && unit !== 'each' ? `Counted in bulk, by ${unit}` : 'Counted in bulk'
}
